import React, { useState, useEffect } from 'react';
import { System, Line, ModalActionType } from '../types';
import Modal from './Modal';
import { useDatabaseContext } from '../contexts/DatabaseContext';

interface SystemFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  actionType: ModalActionType;
  system?: System | null;
  onRefresh: () => void;
}

interface SystemFormData {
  systemId: number | '';
  name: string;
  selectedLineIds: string[];
  description: string;
  showOnDashboard: boolean;
  showOnStations: boolean;
  showOnReports: boolean;
  showOnModes: boolean;
}

type TabType = 'basic' | 'lines' | 'visibility';

const SystemFormModalV2: React.FC<SystemFormModalProps> = ({ 
  isOpen, 
  onClose, 
  actionType, 
  system, 
  onRefresh 
}) => {
  const { apiUrl } = useDatabaseContext();
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [formData, setFormData] = useState<SystemFormData>({
    systemId: '',
    name: '',
    selectedLineIds: [],
    description: '',
    showOnDashboard: true,
    showOnStations: true,
    showOnReports: true,
    showOnModes: true
  });
  const [availableLines, setAvailableLines] = useState<Line[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (actionType === ModalActionType.MODIFY && system) {
      setFormData({
        systemId: system.systemId || '',
        name: system.name,
        selectedLineIds: system.lineIds || [],
        description: system.description || '',
        showOnDashboard: system.showOnDashboard ?? true,
        showOnStations: system.showOnStations ?? true,
        showOnReports: system.showOnReports ?? true,
        showOnModes: system.showOnModes ?? true
      });
    } else if (actionType === ModalActionType.ADD) {
      setFormData({
        systemId: '',
        name: '',
        selectedLineIds: [],
        description: '',
        showOnDashboard: true,
        showOnStations: true,
        showOnReports: true,
        showOnModes: true
      });
    }
    setErrors({});
    // Reset to basic tab when opening
    setActiveTab('basic');
  }, [actionType, system, isOpen]);

  // Fetch available lines when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchLines = async () => {
        try {
          const response = await fetch(`${apiUrl}/lines`);
          if (response.ok) {
            const data = await response.json();
            // Sort lines by hardware ID for better organization
            const sortedLines = (data.data || []).sort((a: Line, b: Line) => {
              if (a.hardwareId === null || a.hardwareId === undefined) {
                if (b.hardwareId === null || b.hardwareId === undefined) {
                  return a.name.localeCompare(b.name);
                }
                return 1;
              }
              if (b.hardwareId === null || b.hardwareId === undefined) {
                return -1;
              }
              const diff = Number(a.hardwareId) - Number(b.hardwareId);
              if (diff === 0) {
                return a.name.localeCompare(b.name);
              }
              return diff;
            });
            setAvailableLines(sortedLines);
          }
        } catch (error) {
          console.error('Error fetching lines:', error);
        }
      };
      fetchLines();
    }
  }, [isOpen, apiUrl]);

  const handleInputChange = (field: keyof SystemFormData, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleLineSelection = (lineId: string, selected: boolean) => {
    setFormData(prev => ({
      ...prev,
      selectedLineIds: selected
        ? [...prev.selectedLineIds, lineId]
        : prev.selectedLineIds.filter(id => id !== lineId)
    }));
    setErrors(prev => ({ ...prev, selectedLines: '' }));
  };

  const handleSelectAllLines = () => {
    setFormData(prev => ({
      ...prev,
      selectedLineIds: availableLines.map(line => line.id)
    }));
    setErrors(prev => ({ ...prev, selectedLines: '' }));
  };

  const handleDeselectAllLines = () => {
    setFormData(prev => ({
      ...prev,
      selectedLineIds: []
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'System name is required.';
    }

    if (formData.selectedLineIds.length === 0) {
      newErrors.selectedLines = 'At least one line must be selected.';
    }

    setErrors(newErrors);
    
    // Switch to the tab with the first error
    if (newErrors.name || newErrors.description) {
      setActiveTab('basic');
    } else if (newErrors.selectedLines) {
      setActiveTab('lines');
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const systemData = {
        name: formData.name.trim(),
        numberOfLines: formData.selectedLineIds.length,
        lineIds: formData.selectedLineIds,
        description: formData.description.trim() || undefined,
        showOnDashboard: formData.showOnDashboard,
        showOnStations: formData.showOnStations,
        showOnReports: formData.showOnReports,
        showOnModes: formData.showOnModes
      };

      const url = actionType === ModalActionType.ADD 
        ? `${apiUrl}/systems`
        : `${apiUrl}/systems/${system?.id}`;

      const method = actionType === ModalActionType.ADD ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(systemData)
      });

      if (response.ok) {
        onRefresh();
        onClose();
      } else {
        const data = await response.json();
        setErrors({ general: data.error || 'An error occurred while saving the system.' });
      }
    } catch (error) {
      setErrors({ general: 'An error occurred while saving the system.' });
    } finally {
      setSaving(false);
    }
  };

  const modalTitle = actionType === ModalActionType.ADD ? 'Add System' : 'Edit System';

  const TabButton: React.FC<{ tab: TabType; label: string; hasError?: boolean }> = ({ tab, label, hasError }) => (
    <button
      type="button"
      onClick={() => setActiveTab(tab)}
      className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
        activeTab === tab
          ? 'border-indigo-600 text-indigo-600'
          : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
      } ${hasError ? 'text-red-600' : ''}`}
    >
      {label}
      {hasError && <span className="ml-1 text-red-600">•</span>}
    </button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="xl"
    >
      <form onSubmit={handleSubmit}>
        {errors.general && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {errors.general}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-slate-200 mb-6">
          <nav className="flex space-x-4">
            <TabButton 
              tab="basic" 
              label="Basic Info" 
              hasError={!!(errors.name || errors.description)}
            />
            <TabButton 
              tab="lines" 
              label="Lines" 
              hasError={!!errors.selectedLines}
            />
            <TabButton 
              tab="visibility" 
              label="Visibility" 
            />
          </nav>
        </div>

        {/* Tab Content - Fixed height for consistency */}
        <div className="h-[500px] overflow-y-auto px-1">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6 py-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  System Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.name ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="e.g., Production System A"
                  required
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  rows={4}
                  placeholder="Optional description of the system's purpose or location..."
                />
                <p className="mt-1 text-xs text-slate-500">
                  Provide details about this system's purpose, location, or special configurations
                </p>
              </div>
            </div>
          )}

          {/* Lines Tab */}
          {activeTab === 'lines' && (
            <div className="space-y-4 py-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Select Lines <span className="text-red-500">*</span>
                  </label>
                  <div className="space-x-2">
                    <button
                      type="button"
                      onClick={handleSelectAllLines}
                      className="text-xs text-indigo-600 hover:text-indigo-800"
                    >
                      Select All
                    </button>
                    <span className="text-slate-400">|</span>
                    <button
                      type="button"
                      onClick={handleDeselectAllLines}
                      className="text-xs text-indigo-600 hover:text-indigo-800"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
                
                <div className="border border-slate-300 rounded-md p-4 max-h-80 overflow-y-auto bg-slate-50">
                  {availableLines.length === 0 ? (
                    <p className="text-slate-500 italic text-center py-8">
                      No lines available. Create lines first.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {availableLines.map((line) => (
                        <label 
                          key={line.id} 
                          className="flex items-center space-x-3 p-3 bg-white hover:bg-slate-100 rounded-lg cursor-pointer transition-colors border border-slate-200"
                        >
                          <input
                            type="checkbox"
                            checked={formData.selectedLineIds.includes(line.id)}
                            onChange={(e) => handleLineSelection(line.id, e.target.checked)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">{line.name}</div>
                            <div className="text-xs text-slate-500 mt-1">
                              {line.hardwareId && (
                                <span className="inline-block mr-3">
                                  HW ID: {line.hardwareId}
                                </span>
                              )}
                              <span className="inline-block mr-3">
                                {line.numberOfStations} stations
                              </span>
                              <span className="inline-block">
                                {line.numberOfProductPockets} pockets
                              </span>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                
                {errors.selectedLines && (
                  <p className="mt-1 text-sm text-red-600">{errors.selectedLines}</p>
                )}
                
                <p className="mt-2 text-xs text-slate-500">
                  Select which production lines this system will control. 
                  {formData.selectedLineIds.length > 0 && (
                    <span className="font-medium">
                      {' '}({formData.selectedLineIds.length} line{formData.selectedLineIds.length !== 1 ? 's' : ''} selected)
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Visibility Tab */}
          {activeTab === 'visibility' && (
            <div className="space-y-4 py-4">
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-4">
                  Page Visibility Settings
                </h3>
                <p className="text-xs text-slate-500 mb-6">
                  Control where this system appears in the application
                </p>
                
                <div className="space-y-4">
                  <label className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.showOnDashboard}
                      onChange={(e) => handleInputChange('showOnDashboard', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-900">Dashboard Page</div>
                      <div className="text-xs text-slate-500 mt-1">
                        Display this system's metrics and status on the main dashboard
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.showOnStations}
                      onChange={(e) => handleInputChange('showOnStations', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-900">Stations Page</div>
                      <div className="text-xs text-slate-500 mt-1">
                        Show this system in the stations management interface for operator assignments
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.showOnReports}
                      onChange={(e) => handleInputChange('showOnReports', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-900">Reports Page</div>
                      <div className="text-xs text-slate-500 mt-1">
                        Include this system's data in reports and analytics views
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.showOnModes}
                      onChange={(e) => handleInputChange('showOnModes', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-900">Modes Page</div>
                      <div className="text-xs text-slate-500 mt-1">
                        Display this system in production mode management (future feature)
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default SystemFormModalV2;